import {FormElement} from "./form-element.js";

/**
 * Represents an option element within a form, extending the functionality of a FormElement.
 * It provides methods for defining the option's properties and behavior.
 */
export class OptionElement extends FormElement {
    componentTag = "option";
    value;
    text;

    optionComponent;

    /**
     * Getter method to retrieve the selected state.
     *
     * @return {boolean} The selected state of the option component. Returns `false` if the option component is not available or the selected state is undefined.
     */
    get selected() {
        return this.optionComponent?.selected ?? false;
    }

    /**
     * Sets the selected state for the option component.
     *
     * @param {boolean} value - The new selected state. If null or undefined, the state defaults to false.
     */
    set selected(value) {
        this.optionComponent.selected = (value ?? false);
    }

    /**
     * Constructs an instance of the class.
     *
     * @param {Object} formData - The data associated with the form.
     * @param {HTMLElement} parentElement - The parent HTML element to attach to.
     * @param {Array} collection - A collection of items related to the instance.
     * @return {void}
     */
    constructor(formData, parentElement, collection) {
        super(formData, parentElement, collection);
    }

    /**
     * Retrieves the parent element of the current component.
     *
     * @return {HTMLElement} The parent element of the component.
     */
    componentWrapper() {
        return this.parentElement;
    }

    /**
     * Updates or sets the label for an element or component.
     *
     * This method allows you to specify a new label or modify
     * an existing one, which can be used for display purposes
     * or to provide descriptive text for the associated element.
     *
     * @param {string} label - The text to set as the label. It should be a non-empty string.
     * @throws {TypeError} Throws an error if the input is not a valid string.
     * @throws {Error} Throws a generic error if the operation fails.
     * @returns {void}
     */
    setLabel() {}

    /**
     * Creates and configures a component by extending from the parent component.
     * Sets the component's value and appends a text node.
     *
     * @return {HTMLElement} The configured option component.
     */
    component() {
        this.optionComponent = super.component();
        this.optionComponent.value = this.value;
        this.optionComponent.appendChild(document.createTextNode(this.text));

        //prevent placeholder items from being selectable after the initial load
        if (this.value == "") {
            this.optionComponent.setAttribute("hidden", "hidden");
        }
        return this.optionComponent;
    }

    /**
     * Destroys the current optionComponent, removing its references
     * and setting the internal state to reflect the destruction.
     *
     * @return {void} Doesn't return a value.
     */
    destroy() {
        this.optionComponent.remove();
        this.optionComponent = null;
        this.destroy = true;
    }
}
